import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import ForgotPassword from "../../../../containers/account/forgotPassword";
import GuestLogin from "./GuestLogin";
import SignUpForm from "./SignUpForm";
import SocailLogin from "./SocailLogin";
import { getUser } from "../../../../helpers/helperMethods";
import styles from "./guestLogin.module.scss";
import {
  CHECKOUT_EMAIL,
  CHECKOUT_SIGNUP,
  GUEST_CHECKOUT_CLICKED,
  ROUTE_CREATE_ACCOUNT,
  ROUTE_LOGIN,
} from "../../../../constants";
import { setShowCart } from "../../../../redux/slices/cartSlice";
import MailBlueGuestLoginModalLoginButtonIcon from "../../../../images/MailBlueGuestLoginModalLoginButton.png";
import {
  brazeLogCustomEventCheckoutLoginAction,
  brazeLogCustomEventGuestCheckoutClicked,
} from "../../../../helpers/brazeHelper";
import useCheckMobileScreen from "../../../../hooks/useCheckMobileScreen";
import { useAppSelector } from "../../../../redux/store/store";
import { PULSE_VIEW_ID, trackPulseView } from "../../../../analytics/pulseViewIds";

interface IGuestCheckoutModalProps {
  showContactForm?: boolean;
  handleCloseContactForm?: () => void;
  onProceedAsGuest?: () => void;
  subTotal: number;
}

const GuestLoginModal = (props: IGuestCheckoutModalProps) => {
  const {
    showContactForm,
    handleCloseContactForm,
    onProceedAsGuest,
    subTotal,
  } = props;
  const currentGuestUserInfo = getUser();
  const navigate = useNavigate();
  const cartDispatch = useDispatch();
  const cart = useAppSelector((state) => state.cart);
  const isItMobile = useCheckMobileScreen();
  const [showGuestContactForm, setShowGuestContactForm] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [showSignUp, setshowSignUp] = useState<boolean>(false);
  const trackedCheckoutEventsRef = useRef<Set<string>>(new Set());

  // Allow the same events again on a later modal open; block duplicate fires in one session
  useEffect(() => {
    if (!showContactForm) {
      trackedCheckoutEventsRef.current.clear();
    }
  }, [showContactForm]);

  const guestCheckoutHandle = () => {
    onProceedAsGuest?.();
  };

  const trackCheckoutLoginAction = (eventName: string) => {
    if (trackedCheckoutEventsRef.current.has(eventName)) return;
    trackedCheckoutEventsRef.current.add(eventName);
    brazeLogCustomEventCheckoutLoginAction(eventName, cart, subTotal, isItMobile);
  };

  const guestCheckoutButtonHandle = () => {
    if (!trackedCheckoutEventsRef.current.has(GUEST_CHECKOUT_CLICKED)) {
      trackedCheckoutEventsRef.current.add(GUEST_CHECKOUT_CLICKED);
      brazeLogCustomEventGuestCheckoutClicked(cart, subTotal, isItMobile);
    }
    onProceedAsGuest?.();
  };

  const handleSignupClick = () => {
    trackCheckoutLoginAction(CHECKOUT_SIGNUP);
    handleCloseModal();
    cartDispatch(setShowCart(false));
    navigate(ROUTE_CREATE_ACCOUNT);
  };

  const handleSignInClick = () => {
    setshowSignUp(false);
  };

  const handleCloseModal = () => {
    handleCloseContactForm?.();
    setShowGuestContactForm(false);
    setShowForgotPassword(false);
    setshowSignUp(false);
  };

  const handleEmailLogin = () => {
    trackCheckoutLoginAction(CHECKOUT_EMAIL);
    handleCloseModal();
    cartDispatch(setShowCart(false));
    navigate(ROUTE_LOGIN);
  };

  const isDefaultLoginView =
    !showGuestContactForm && !showForgotPassword && !showSignUp;

  // Default Login / Guest Checkout modal open (GuestLogin.tsx only mounts on nested contact form)
  useEffect(() => {
    if (showContactForm && isDefaultLoginView) {
      trackPulseView(PULSE_VIEW_ID.GUEST_LOGIN_MODAL, {
        page_path: window.location.pathname,
      });
    }
  }, [showContactForm, isDefaultLoginView]);

  return (
    <Modal
      show={showContactForm}
      onHide={handleCloseModal}
      backdropClassName="guest_login_modal_backdrop"
      className={`theme-modal-style p-0 app_container_wrap nor-padding border-radius-15 checkout-modal-mob ${styles.guest_login_modal}`}
      centered
    >
      <Modal.Header className="flex-column pt-4 pb-0 position-relative border-0">
        <button
          type="button"
          className="btn modal-close-button pe-0"
          onClick={handleCloseModal}
        >
          Close
        </button>
        {!isDefaultLoginView && (
          <h5 className="f-s22 font-Cls text-center mx-auto text-capitalize full_width_mob clr-dark-grey mb-0">
            {showSignUp ? "Create A Loyalty Account" : "Log In"}
          </h5>
        )}
      </Modal.Header>
      <Modal.Body className="pb-md-4 pb-3 pt-0">
        {showGuestContactForm ? (
          <GuestLogin
            closeModal={handleCloseModal}
            currentGuestUserInfo={currentGuestUserInfo}
            subTotal={subTotal}
          />
        ) : showForgotPassword ? (
          <ForgotPassword isGuestModal={true} paddingTop={styles.forgotPasswordModal} />
        ) : showSignUp ? (
          <SignUpForm closeModal={handleCloseModal} />
        ) : (
          <div className={styles.loginModalContent}>
            <h2 className={`${styles.loginModalTitle} font-helvetica-black-extended`}>Login</h2>
            <SocailLogin
              variant="checkout"
              onEmailClick={handleEmailLogin}
              onCheckoutLoginTrack={trackCheckoutLoginAction}
              guestCheckoutHandle={guestCheckoutHandle}
              isGuestCheckout={true}
              emailIcon={
                <img
                  src={MailBlueGuestLoginModalLoginButtonIcon}
                  alt=""
                  aria-hidden="true"
                />
              }
            />
            <p className={`${styles.signUpText} font-helvetica-53-extended`}>
              Earn <strong className="font-helvetica-black-extended">FREE</strong> Food!{" "}
              <a
                onClick={handleSignupClick}
                className={styles.signUpLink}
                role="button"
                tabIndex={0}
              >
                Sign Up
              </a>
            </p>
            {Boolean(process.env.REACT_APP_ENABLE_GUEST_CHECKOUT) && (
              <>
                <span
                  className={`content-divider login-content-divider guest-checkout-or-divider my-0 d-flex ${styles.orDivider}`}
                >
                  <span className="w-auto px-3 font-helvetica-53-extended">or</span>
                </span>
                <button
                  type="button"
                  onClick={guestCheckoutButtonHandle}
                  className={`btn btn-large d-block mx-auto cursor-pointer ${styles.guestCheckoutBtn} font-helvetica-black-extended`}
                >
                  <span className={`${styles.guestCheckoutBtnText} font-helvetica-53-extended`}>
                    Guest Checkout
                  </span>
                </button>
              </>
            )}
          </div>
        )}
        {showSignUp && (
          <p className={`${styles.signUpText} font-helvetica-light text-center pt-2`}>
            Already have an account?{" "}
            <a
              onClick={handleSignInClick}
              className={styles.signUpLink}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  handleSignInClick();
                }
              }}
            >
              Log In
            </a>
          </p>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default GuestLoginModal;
